/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import authenticatedFetch from "../api/authenticatedFetch";
import { buildQueryParams } from "./buildQueryParams";

/**
 * The download half of every report export: the format list both pickers offer, URL assembly,
 * the server's own filename, and handing the bytes to the browser.
 *
 * Shared rather than copied per report. The Daily Logbook, Daily Sales and Group Labor Summary exports queue
 * behind the same server-side render slot and answer with the same statuses, so the retry copy, the
 * Content-Disposition parsing and the blob handling are one fact about the machine — two copies
 * would let them drift. Only the URLs differ, and each report builds its own.
 */
export const EXPORT_FORMATS = [
  { value: "pdf", label: "PDF" },
  { value: "csv", label: "CSV" },
];

export const DEFAULT_EXPORT_FORMAT = "pdf";

export const EXPORT_ERROR_MESSAGE = "We could not generate this export. Please try again.";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api";

/** Every report's CSV goes through the one registry-backed route; only `report` differs. */
export const CSV_EXPORT_PATH = "/v1/admin/csv_reports/export";

/** Absolute URL for an API path, with the undefined params dropped rather than sent empty. */
export const withApiQuery = (path, params) => {
  const query = buildQueryParams(params);
  return query ? `${API_BASE_URL}${path}?${query}` : `${API_BASE_URL}${path}`;
};

export const toFilenameSlug = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Reads the server's own filename, preferring the RFC 5987 form when both are present.
 */
export const filenameFromDisposition = (header) => {
  if (!header) return null;

  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch (error) {
      return null;
    }
  }

  // The lookahead keeps `filename*=<other charset>` out of the plain branch, which would
  // otherwise save the raw parameter as the filename.
  const plain = header.match(/filename\s*=\s*(?!\*)"?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
};

/**
 * Hands the blob to the browser as a download. The anchor is attached before the click and
 * removed after, which is what Firefox needs to honour `download`. Cleanup runs even if the
 * click throws, so a blocked download leaves no stray node or retained blob behind.
 *
 * The object URL is revoked on the next tick rather than inline: Firefox and Safari can still
 * be reading the blob when the click returns, and these documents run to several MB.
 */
export const triggerBrowserDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);

  try {
    anchor.click();
  } finally {
    document.body.removeChild(anchor);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
  }
};

/**
 * Fetches an export and saves it, returning the filename used. Throws the enriched error
 * `authenticatedFetch` builds, so the caller can show the server's own message — the scope
 * cap (422) and the "another report is being generated" back-off (429) both matter to the
 * user and neither is a generic failure.
 */
export const downloadExport = async ({ url, fallbackFilename }) => {
  const { response } = await authenticatedFetch(url, {
    method: "GET",
    defaultErrorMessage: EXPORT_ERROR_MESSAGE,
  });

  const blob = await response.blob();
  const filename =
    filenameFromDisposition(response?.headers?.get?.("content-disposition")) || fallbackFilename;

  triggerBrowserDownload(blob, filename);

  return filename;
};
